import { useEffect, useMemo, useState } from "react";

import { Link, useLocation } from "react-router-dom";

import AdminShell from "../components/AdminShell";

import AdminCrudPanel from "../components/AdminCrudPanel";

import { getTenantAdmin, getTenantAudit, getTenantGovernanceConfig, getPrograms, inviteTenantUser, updateTenantGovernanceConfig, updateTenantUserAuthority, associateCandidate, getBusinessUnits, createBusinessUnit } from "../lib/api";

import { panelFromPath } from "../lib/admin";

import { TENANT_PANELS, seedTenantRows } from "../lib/adminCrudConfig";



const FALLBACK_NAV = [

  { id: "overview", label: "Tenant console", route: "/admin/tenant" },

  { id: "company", label: "Company", route: "/admin/tenant/company" },

  { id: "units", label: "Business units", route: "/admin/tenant/units" },

  { id: "products", label: "Products", route: "/admin/tenant/products" },

  { id: "projects", label: "Projects", route: "/admin/tenant/projects" },

  { id: "applications", label: "Applications", route: "/admin/tenant/applications" },

  { id: "users", label: "Users & teams", route: "/admin/tenant/users" },

  { id: "roles", label: "Roles / RBAC", route: "/admin/tenant/roles" },

  { id: "sources", label: "Data sources", route: "/admin/tenant/sources" },

  { id: "governance", label: "Governance", route: "/admin/tenant/governance" },

  { id: "reports", label: "Reports", route: "/admin/tenant/reports" },

  { id: "audit", label: "Audit history", route: "/admin/tenant/audit" },

];



export default function AdminTenant({ user, onLogout }) {

  const location = useLocation();

  const panel = panelFromPath(location.pathname, "/admin/tenant");

  const panelConfig = TENANT_PANELS[panel] || TENANT_PANELS.overview;

  const [data, setData] = useState(null);

  const [auditEvents, setAuditEvents] = useState([]);

  const [error, setError] = useState("");

  const [invite, setInvite] = useState({ name: "", email: "", role: "Enterprise Architect", password: "" });

  const [inviteMsg, setInviteMsg] = useState("");

  const [govConfig, setGovConfig] = useState(null);
  const [govMsg, setGovMsg] = useState("");
  const [govBusy, setGovBusy] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);

  useEffect(() => {
    getTenantAdmin()
      .then(({ data: payload }) => setData(payload))
      .catch((err) => setError(err.response?.data?.detail || "Could not load tenant console"));
    getPrograms()
      .then(({ data: payload }) => setPrograms(payload.programs || payload.items || []))
      .catch(() => setPrograms([]));
    getBusinessUnits()
      .then(({ data: payload }) => setBusinessUnits(payload.business_units || []))
      .catch(() => setBusinessUnits([]));
  }, []);



  useEffect(() => {

    if (panel === "governance") {

      getTenantGovernanceConfig()

        .then(({ data: payload }) => setGovConfig(payload))

        .catch((err) => setError(err.response?.data?.detail || "Could not load governance configuration"));

    }

    if (panel === "audit") {

      getTenantAudit()

        .then(({ data: payload }) => setAuditEvents(payload.events || []))

        .catch(() => setAuditEvents([]));

    }

  }, [panel]);



  const company = data?.company || {};

  const nav = data?.nav || FALLBACK_NAV;

  const seedRows = useMemo(() => {
    if (panel === "units" && businessUnits.length) {
      return businessUnits;
    }
    return seedTenantRows(panel, data, auditEvents);
  }, [panel, data, auditEvents, businessUnits]);

  const refreshTenant = () => getTenantAdmin().then(({ data: payload }) => setData(payload));
  const refreshUnits = () => getBusinessUnits().then(({ data: payload }) => setBusinessUnits(payload.business_units || []));

  const persistTenantRow = async (action, payload, rationale) => {
    if (panel === "units" && action === "create") {
      await createBusinessUnit({
        id: payload.id || undefined,
        name: payload.name,
        owner: payload.owner,
      });
      await refreshUnits();
      return;
    }
    if (panel === "users" && (action === "edit" || action === "access")) {
      const email = payload.email || payload.row?.email;
      if (!email) throw new Error("User email required");
      const decisions = {};
      ["G1", "G2", "G4", "ARCHITECTURE", "SECURITY", "DELIVERY_READINESS", "G7", "G8"].forEach((key) => {
        const val = payload[`auth_${key}`] || payload.decisions?.[key];
        if (val) decisions[key] = val;
      });
      await updateTenantUserAuthority(email, { decisions, note: rationale });
      await refreshTenant();
      return;
    }
    if (panel === "applications" && action === "move") {
      await associateCandidate(payload.id, { program_id: payload.program_id, rationale });
      await refreshTenant();
    }
  };

  const saveGovernance = async (patch) => {
    setGovBusy(true);
    setGovMsg("");
    try {
      const { data } = await updateTenantGovernanceConfig(patch);
      setGovConfig((prev) => ({ ...prev, resolved: data.resolved, tenant_patch: data.tenant_patch }));
      setGovMsg("Governance configuration saved. Workspace apps inherit on next load.");
    } catch (err) {
      setGovMsg(err.response?.data?.detail || "Could not save governance configuration");
    } finally {
      setGovBusy(false);
    }
  };



  return (

    <AdminShell kind="tenant" user={user} nav={nav} onLogout={onLogout}>

      {error && <div className="error-message">{error}</div>}

      {!data && !error && <div className="loading-screen" data-testid="page-wait">Loading tenant console…</div>}

      {data && (

        <div className="dashboard" data-testid="tenant-panel" data-panel={panel}>

          <div className="intro-row">

            <div>

              <p className="kicker">YUG360-MIND ADMIN PLATFORM · TENANT CONSOLE</p>

              <h1>{company.customer || "Customer"} <em>administration</em></h1>

              <p className="intro-copy">

                Scoped to {company.code}. Configure company, units, products, users and integrations.

                Evidence quality, 7R and MDR authorization stay in the governed product workspace.

              </p>

            </div>

            <Link className="primary-button" data-testid="tenant-open-workspace" to="/">Open governed workspace</Link>

          </div>

          <p className="admin-note">{data.note}</p>



          {panel === "overview" && (

            <section className="admin-section">

              <p className="kicker">{panelConfig.kicker}</p>

              <h2 className="admin-crud-title">{panelConfig.title}</h2>

              <p className="muted">{panelConfig.detail}</p>
              <p className="muted small-note admin-crud-hint" style={{ marginTop: 8 }}>
                Row listings show always-visible <strong>Actions</strong> — View, Access, Move, Modify and Delete as applicable.
                Governed changes require review and approval rationale.
              </p>

              <div className="metric-grid" style={{ marginTop: 18 }}>

                {[

                  ["Applications", data.application_count],

                  ["Business units", (company.units || []).length],

                  ["Users", (data.directory || []).length],

                  ["Integrations", (company.integrations || []).length],

                ].map(([label, value]) => (

                  <div key={label} className="metric"><div><small>{label}</small><strong>{value}</strong></div></div>

                ))}

              </div>

            </section>

          )}



          {panel === "users" && (

            <section className="admin-section" data-testid="tenant-invite-block">

              <form

                className="why-form"

                data-testid="invite-user-form"

                onSubmit={async (e) => {

                  e.preventDefault();

                  setInviteMsg("");

                  try {

                    const body = { name: invite.name, email: invite.email, role: invite.role };

                    if (invite.password) body.password = invite.password;

                    const { data: result } = await inviteTenantUser(body);

                    const temp = result.temporary_password ? ` Temporary password: ${result.temporary_password}` : "";

                    setInviteMsg(`Invited ${result.user?.email}.${temp}`);

                    setInvite({ name: "", email: "", role: invite.role, password: "" });

                    await refreshTenant();

                  } catch (err) {

                    setInviteMsg(err.response?.data?.detail || "Invite failed");

                  }

                }}

              >

                <p className="kicker">INVITE OPERATOR · OR USE ADD NEW USER BELOW</p>

                <label>Name<input data-testid="invite-name" required value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} /></label>

                <label>Email<input data-testid="invite-email" type="email" required value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} /></label>

                <label>Role

                  <select data-testid="invite-role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>

                    {(data.inviteable_roles || ["Enterprise Architect", "Product Owner"]).map((role) => (

                      <option key={role} value={role}>{role}</option>

                    ))}

                  </select>

                </label>

                <label>Password (optional)<input data-testid="invite-password" type="password" value={invite.password} onChange={(e) => setInvite({ ...invite, password: e.target.value })} /></label>

                <button data-testid="invite-submit" className="secondary-button" type="submit">Invite via API</button>

                {inviteMsg && <p className="admin-note" data-testid="invite-result">{inviteMsg}</p>}

              </form>

            </section>

          )}



          {panel !== "overview" && panel !== "governance" && panelConfig.entityLabel != null && (

            <AdminCrudPanel

              panelConfig={panelConfig}

              rows={seedRows}

              onPersist={(panel === "users" || panel === "applications" || panel === "units") ? persistTenantRow : undefined}

              contextOptions={{ programs }}

              testId={`tenant-crud-${panel}`}

            />

          )}

          {panel === "governance" && (

            <section className="admin-section" data-testid="tenant-governance-config">

              <p className="kicker">{panelConfig.kicker}</p>

              <p className="admin-note">

                Configuration defines rules teams inherit. G1/MDR decisions are immutable records — not editable here.

              </p>

              {govConfig?.resolved?.g1 && (
                <>
                <article className="admin-card">
                  <strong>G1 — {govConfig.resolved.g1.title}</strong>
                  <p>{govConfig.resolved.g1.purpose}</p>
                  <p className="muted small-note">
                    Authority: {(govConfig.resolved.g1.decision_authority || []).join(", ")} ·
                    AI: {govConfig.resolved.g1.ai_role}
                  </p>
                  <p className="kicker">REQUIRED AREAS</p>
                  <ul className="admin-list">
                    {(govConfig.resolved.g1.required_areas || []).map((item) => (
                      <li key={item}>{item.replaceAll("_", " ")}</li>
                    ))}
                  </ul>
                  <p className="kicker">BLOCKING CONDITIONS</p>
                  <ul className="admin-list">
                    {(govConfig.resolved.g1.blocking_conditions || []).map((item) => (
                      <li key={item}>{item.replaceAll("_", " ")}</li>
                    ))}
                  </ul>
                </article>
                <GovernanceEditor
                  resolved={govConfig.resolved}
                  busy={govBusy}
                  message={govMsg}
                  onSave={saveGovernance}
                />
                </>
              )}

            </section>
          )}

        </div>
      )}
    </AdminShell>
  );
}

const GOVERNANCE_ROLE_OPTIONS = [
  "Product Owner",
  "Business Owner",
  "Enterprise Architect",
  "Solution Architect",
  "Modernization Lead",
  "Security Architect",
  "Program Manager",
  "CIO",
  "Executive Sponsor",
];

function GovernanceEditor({ resolved, busy, message, onSave }) {
  const [g1Roles, setG1Roles] = useState(resolved?.g1?.decision_authority || []);
  const [g2Roles, setG2Roles] = useState(resolved?.decision_authorities?.G2?.approver_roles || []);
  const [coverage, setCoverage] = useState(resolved?.g1?.min_evidence_coverage_pct ?? 60);

  useEffect(() => {
    setG1Roles(resolved?.g1?.decision_authority || []);
    setG2Roles(resolved?.decision_authorities?.G2?.approver_roles || []);
    setCoverage(resolved?.g1?.min_evidence_coverage_pct ?? 60);
  }, [resolved]);

  const toggleRole = (list, setList, role) => {
    setList(list.includes(role) ? list.filter((r) => r !== role) : [...list, role]);
  };

  const submit = (e) => {
    e.preventDefault();
    onSave({
      g1: { decision_authority: g1Roles, min_evidence_coverage_pct: Number(coverage) },
      decision_authorities: { G2: { approver_roles: g2Roles } },
    });
  };

  return (
    <form className="admin-card" data-testid="governance-editor" onSubmit={submit}>
      <strong>Edit tenant governance</strong>
      <p className="muted small-note">Changes apply to new sessions and app loads. Past G1/MDR seals stay immutable.</p>
      <p className="kicker">G1 DECISION AUTHORITY</p>
      <div className="admin-chip-row">
        {GOVERNANCE_ROLE_OPTIONS.map((role) => (
          <label key={role} className="admin-chip">
            <input
              type="checkbox"
              checked={g1Roles.includes(role)}
              onChange={() => toggleRole(g1Roles, setG1Roles, role)}
            />
            {role}
          </label>
        ))}
      </div>
      <label>
        Minimum evidence coverage (%)
        <input
          type="number"
          min={0}
          max={100}
          value={coverage}
          onChange={(e) => setCoverage(e.target.value)}
          data-testid="governance-coverage"
        />
      </label>
      <p className="kicker">G2 / MDR APPROVER ROLES</p>
      <div className="admin-chip-row">
        {GOVERNANCE_ROLE_OPTIONS.map((role) => (
          <label key={`g2-${role}`} className="admin-chip">
            <input
              type="checkbox"
              checked={g2Roles.includes(role)}
              onChange={() => toggleRole(g2Roles, setG2Roles, role)}
            />
            {role}
          </label>
        ))}
      </div>
      {message && <p className="admin-note">{message}</p>}
      <button type="submit" className="primary-button" disabled={busy} data-testid="governance-save">
        {busy ? "Saving…" : "Save governance configuration"}
      </button>
    </form>
  );
}

